import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Clock, Award, Users, Play, Lock, ChevronRight, ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn, formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Skeleton from '@/components/ui/Skeleton';
import DocumentHead from '@/components/DocumentHead';

const LEVEL_BADGE_VARIANT: Record<string, 'green' | 'yellow' | 'red'> = {
  beginner: 'green',
  intermediate: 'yellow',
  advanced: 'red',
};

function useCourse(slug: string) {
  return useQuery({
    queryKey: ['course', slug],
    queryFn: () =>
      api.get<any>(`/courses/${slug}`),
    enabled: !!slug,
  });
}

function useCourseEnrollmentCheck(slug: string) {
  return useQuery({
    queryKey: ['course-enrollment-check', slug],
    queryFn: () =>
      api.get<{ enrolled: boolean; enrollment?: any }>(`/courses/${slug}/check`),
    enabled: !!slug,
  });
}

export default function CourseDetail() {
  const { t } = useTranslation('courses');
  const { slug } = useParams<{ slug: string }>();
  const { data: course, isLoading } = useCourse(slug || '');
  const { data: enrollmentData } = useCourseEnrollmentCheck(slug || '');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const isEnrolled = enrollmentData?.enrolled ?? false;

  const levelLabel = useCallback(
    (key: string) => {
      const localized = t(`detail.levels.${key}`, { defaultValue: '' });
      if (localized) return localized;
      return key.charAt(0).toUpperCase() + key.slice(1);
    },
    [t],
  );

  function toggleModule(moduleId: string) {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="bg-[--bg-warm] min-h-screen">
        <div className="bg-cta py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Skeleton width="120px" height="24px" className="mb-4 !bg-white/20" />
            <Skeleton width="60%" height="36px" className="mb-3 !bg-white/20" />
            <Skeleton width="80%" height="20px" className="!bg-white/20" />
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton height="60px" className="w-full" />
              <Skeleton height="200px" className="w-full" />
              <Skeleton height="300px" className="w-full" />
            </div>
            <div>
              <Skeleton height="300px" className="w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!course || course.error) {
    return (
      <div className="bg-[--bg-warm] min-h-screen flex items-center justify-center">
        <div className="text-center py-16">
          <BookOpen className="mx-auto h-16 w-16 text-ink-muted" aria-hidden="true" />
          <h1 className="mt-4 text-2xl font-bold text-ink">{t('detail.notFound.heading')}</h1>
          <p className="mt-2 text-ink-soft">{t('detail.notFound.body')}</p>
          <Link to="/courses">
            <Button variant="boutique-outline" className="mt-6">
              {t('detail.notFound.browseCourses')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const modules = course.modules || [];
  const totalLessons = modules.reduce(
    (sum: number, mod: any) => sum + (mod.lessons?.length || 0),
    0,
  );
  const totalDuration = modules.reduce(
    (sum: number, mod: any) =>
      sum +
      (mod.lessons || []).reduce(
        (s: number, l: any) => s + (l.duration || 0),
        0,
      ),
    0,
  );
  const durationHours = Math.floor(totalDuration / 60);
  const durationMinutes = totalDuration % 60;
  const totalDurationLabel =
    durationHours > 0
      ? t('detail.stats.durationHoursMinutes', { hours: durationHours, minutes: durationMinutes })
      : t('detail.stats.durationMinutes', { minutes: durationMinutes });

  const hasFoundersPricing =
    course.founders_pricing_id && course.slots_used < course.max_slots;
  const displayPrice = hasFoundersPricing
    ? Number(course.founders_price)
    : Number(course.price);
  const originalPrice = Number(course.price);
  const compareAtPrice = course.compare_at_price
    ? Number(course.compare_at_price)
    : null;
  const isFree = course.price_type === 'free';

  const slotsRemaining = hasFoundersPricing
    ? course.max_slots - course.slots_used
    : null;

  return (
    <div className="bg-[--bg-warm] min-h-screen">
      <DocumentHead
        title={course.name}
        description={course.short_description || course.name}
        canonicalUrl={`/courses/${course.slug}`}
      />

      {/* Hero */}
      <div className="bg-cta text-white py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <nav aria-label={t('detail.breadcrumbs.label')} className="mb-6">
            <ol className="flex items-center gap-1.5 text-sm text-white/60">
              <li>
                <Link to="/" className="hover:text-white transition-colors">
                  {t('detail.breadcrumbs.home')}
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight className="h-4 w-4" />
              </li>
              <li>
                <Link to="/courses" className="hover:text-white transition-colors">
                  {t('detail.breadcrumbs.courses')}
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight className="h-4 w-4" />
              </li>
              <li>
                <span className="text-white font-medium" aria-current="page">
                  {course.name}
                </span>
              </li>
            </ol>
          </nav>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
            <div className="flex-1">
              {/* Level badge */}
              {course.level && (
                <Badge
                  variant={LEVEL_BADGE_VARIANT[course.level] || 'gray'}
                  className="mb-3"
                >
                  {levelLabel(course.level)}
                </Badge>
              )}

              <h1 className="font-display text-3xl lg:text-4xl font-bold">
                {course.name}
              </h1>

              {course.short_description && (
                <p className="mt-3 text-lg text-white/80 max-w-2xl">
                  {course.short_description}
                </p>
              )}
            </div>

            {/* Price & CTA (desktop) */}
            <div className="hidden lg:block shrink-0 w-80">
              <div className="rounded-xl bg-white/10 backdrop-blur p-6">
                {/* Price */}
                <div className="mb-4">
                  {isFree ? (
                    <span className="text-3xl font-bold">{t('detail.hero.free')}</span>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">
                        {formatCurrency(displayPrice)}
                      </span>
                      {hasFoundersPricing && displayPrice < originalPrice && (
                        <span className="text-lg text-white/50 line-through">
                          {formatCurrency(originalPrice)}
                        </span>
                      )}
                      {!hasFoundersPricing && compareAtPrice && compareAtPrice > originalPrice && (
                        <span className="text-lg text-white/50 line-through">
                          {formatCurrency(compareAtPrice)}
                        </span>
                      )}
                    </div>
                  )}
                  {hasFoundersPricing && (
                    <p className="mt-1 text-sm text-amber-300">
                      {t('detail.hero.foundersRemaining', { count: slotsRemaining ?? 0 })}
                    </p>
                  )}
                </div>

                {/* CTA */}
                {isEnrolled ? (
                  <Link to={`/learn/${course.slug}`} className="block">
                    <Button variant="primary" size="lg" className="w-full">
                      {t('detail.hero.goToCourse')}
                    </Button>
                  </Link>
                ) : (
                  <Link to={`/courses/${course.slug}/enroll`} className="block">
                    <Button variant="primary" size="lg" className="w-full">
                      {isFree ? t('detail.hero.enrollFree') : t('detail.hero.enrollNow')}
                    </Button>
                  </Link>
                )}

                {course.access_type && (
                  <p className="mt-3 text-center text-sm text-white/60">
                    {course.access_type === 'lifetime'
                      ? t('detail.hero.lifetimeAccess')
                      : t('detail.hero.accessLabel', { type: course.access_type })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="border-b border-line bg-surface">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-6 py-4">
            <div className="flex items-center gap-2 text-sm text-ink-soft">
              <BookOpen className="h-4 w-4 text-[var(--color-primary-600)]" aria-hidden="true" />
              {t('detail.stats.lessons', { count: totalLessons })}
            </div>
            {totalDuration > 0 && (
              <div className="flex items-center gap-2 text-sm text-ink-soft">
                <Clock className="h-4 w-4 text-[var(--color-primary-600)]" aria-hidden="true" />
                {t('detail.stats.totalDuration', { duration: totalDurationLabel })}
              </div>
            )}
            {course.level && (
              <div className="flex items-center gap-2 text-sm text-ink-soft">
                <Award className="h-4 w-4 text-[var(--color-primary-600)]" aria-hidden="true" />
                <span className="font-medium">{levelLabel(course.level)}</span>
              </div>
            )}
            {course.access_type && (
              <div className="flex items-center gap-2 text-sm text-ink-soft">
                <Users className="h-4 w-4 text-[var(--color-primary-600)]" aria-hidden="true" />
                <span className="font-medium capitalize">{course.access_type}</span>{' '}
                {t('detail.stats.accessSuffix')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            {course.description && (
              <section>
                <h2 className="text-2xl font-bold text-ink mb-4">
                  {t('detail.about')}
                </h2>
                <div
                  className="prose prose-stone dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: course.description }}
                />
              </section>
            )}

            {/* Curriculum */}
            {modules.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-ink mb-4">
                  {t('detail.curriculum.heading')}
                </h2>
                <p className="text-sm text-ink-muted mb-4">
                  {t('detail.curriculum.modulesAndLessons', { count: modules.length })}
                  {' '}&middot;{' '}
                  {t('detail.stats.lessons', { count: totalLessons })}
                  {totalDuration > 0 && (
                    <> &middot; {t('detail.stats.totalDuration', { duration: totalDurationLabel })}</>
                  )}
                </p>

                <div className="rounded-xl border border-line bg-surface overflow-hidden divide-y divide-line">
                  {modules.map((mod: any, modIndex: number) => {
                    const isExpanded = expandedModules.has(mod.id);
                    const lessons = mod.lessons || [];
                    const moduleDuration = lessons.reduce(
                      (s: number, l: any) => s + (l.duration || 0),
                      0,
                    );

                    return (
                      <div key={mod.id}>
                        <button
                          onClick={() => toggleModule(mod.id)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-surface-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary-600)]"
                          aria-expanded={isExpanded}
                        >
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium text-ink-muted uppercase tracking-wider">
                              {t('detail.curriculum.moduleLabel', { number: modIndex + 1 })}
                            </span>
                            <h3 className="font-semibold text-ink truncate">
                              {mod.title}
                            </h3>
                            <p className="text-xs text-ink-muted mt-0.5">
                              {t('detail.curriculum.moduleLessons', { count: lessons.length })}
                              {moduleDuration > 0 &&
                                t('detail.curriculum.moduleDuration', { minutes: moduleDuration })}
                            </p>
                          </div>
                          <ChevronDown
                            className={cn(
                              'h-5 w-5 text-ink-muted shrink-0 ml-2 transition-transform',
                              isExpanded && 'rotate-180',
                            )}
                            aria-hidden="true"
                          />
                        </button>

                        {isExpanded && lessons.length > 0 && (
                          <div className="bg-surface-soft border-t border-line">
                            {lessons.map((lesson: any) => (
                              <div
                                key={lesson.id}
                                className="flex items-center gap-3 px-5 py-3 border-b border-line last:border-0"
                              >
                                {lesson.is_preview ? (
                                  <Play className="h-4 w-4 text-[var(--color-primary-600)] shrink-0" aria-hidden="true" />
                                ) : (
                                  <Lock className="h-4 w-4 text-ink-muted shrink-0" aria-hidden="true" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <span className={cn(
                                    'text-sm truncate block',
                                    lesson.is_preview
                                      ? 'text-ink'
                                      : 'text-ink-muted',
                                  )}>
                                    {lesson.title}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {lesson.is_preview && (
                                    <span className="text-xs font-medium text-[var(--color-primary-700)] bg-[var(--color-primary-50)] px-1.5 py-0.5 rounded">
                                      {t('detail.curriculum.previewBadge')}
                                    </span>
                                  )}
                                  {lesson.duration != null && (
                                    <span className="text-xs text-ink-muted">
                                      {t('detail.curriculum.lessonDuration', { minutes: lesson.duration })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar (mobile-only price card, desktop has it in hero) */}
          <div className="lg:hidden">
            <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
              <div className="mb-4">
                {isFree ? (
                  <span className="text-2xl font-bold text-sage-700">{t('detail.hero.free')}</span>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[var(--color-primary-700)]">
                      {formatCurrency(displayPrice)}
                    </span>
                    {hasFoundersPricing && displayPrice < originalPrice && (
                      <span className="text-sm text-ink-muted line-through">
                        {formatCurrency(originalPrice)}
                      </span>
                    )}
                    {!hasFoundersPricing && compareAtPrice && compareAtPrice > originalPrice && (
                      <span className="text-sm text-ink-muted line-through">
                        {formatCurrency(compareAtPrice)}
                      </span>
                    )}
                  </div>
                )}
                {hasFoundersPricing && (
                  <p className="mt-1 text-sm text-amber-600">
                    {t('detail.hero.foundersRemaining', { count: slotsRemaining ?? 0 })}
                  </p>
                )}
              </div>

              {isEnrolled ? (
                <Link to={`/learn/${course.slug}`} className="block">
                  <Button variant="primary" size="lg" className="w-full">
                    {t('detail.hero.goToCourse')}
                  </Button>
                </Link>
              ) : (
                <Link to={`/courses/${course.slug}/enroll`} className="block">
                  <Button variant="primary" size="lg" className="w-full">
                    {isFree ? t('detail.hero.enrollFree') : t('detail.hero.enrollNow')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Sticky bottom CTA on mobile */}
      <div className="fixed bottom-0 inset-x-0 lg:hidden z-40 bg-surface border-t border-line px-4 py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between gap-4">
          <div>
            {isFree ? (
              <span className="text-lg font-bold text-sage-700">{t('detail.stickyMobile.free')}</span>
            ) : (
              <div className="flex items-baseline gap-1.5">
                <span className="text-lg font-bold text-[var(--color-primary-700)]">
                  {formatCurrency(displayPrice)}
                </span>
                {((hasFoundersPricing && displayPrice < originalPrice) ||
                  (!hasFoundersPricing && compareAtPrice && compareAtPrice > originalPrice)) && (
                  <span className="text-xs text-ink-muted line-through">
                    {formatCurrency(hasFoundersPricing ? originalPrice : compareAtPrice!)}
                  </span>
                )}
              </div>
            )}
          </div>
          {isEnrolled ? (
            <Link to={`/learn/${course.slug}`}>
              <Button variant="primary" size="md">
                {t('detail.stickyMobile.goToCourse')}
              </Button>
            </Link>
          ) : (
            <Link to={`/courses/${course.slug}/enroll`}>
              <Button variant="primary" size="md">
                {isFree ? t('detail.stickyMobile.enrollFree') : t('detail.stickyMobile.enrollNow')}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Spacer for sticky bar */}
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
